import Navigation from '@/components/feature/Navigation';
import SEOHead from '@/components/feature/SEOHead';
import { Link } from 'react-router-dom';
import { getProjectsBySubSection } from '@/mocks/projects';
import { useScrollReveal, revealClass } from '@/hooks/useScrollReveal';

const researchFilmSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': 'https://oludamolaadebowale.com/research-film-projects#collectionpage',
  url: 'https://oludamolaadebowale.com/research-film-projects',
  name: 'Research Film Projects — Oludamola Adebowale',
  description: 'Selection of research film projects contributed to by Nigerian cultural historian and archivist Oludamola Adebowale, including Funmilayo Ransome-Kuti, The Man Died, and Formation — documentary research films exploring Nigerian history, culture, and identity.',
  mainEntity: { '@id': 'https://oludamolaadebowale.com/#person' },
  about: { '@id': 'https://oludamolaadebowale.com/#person' },
  inLanguage: 'en',
  dateModified: '2026-04-05',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://oludamolaadebowale.com/' },
      { '@type': 'ListItem', position: 2, name: 'Research Film Projects', item: 'https://oludamolaadebowale.com/research-film-projects' },
    ],
  },
};

export default function ResearchFilmPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal<HTMLDivElement>();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.05 });

  const filmProjects = getProjectsBySubSection('film');

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Research Film Projects | Oludamola Adebowale | Nigerian Cultural Historian"
        description="Research film projects by Oludamola Adebowale, including Funmilayo Ransome-Kuti, The Man Died, and Formation, documentary research films on Nigerian history and culture."
        keywords="Oludamola Adebowale research films, Nigerian documentary history, Funmilayo Ransome Kuti film, The Man Died Wole Soyinka, Nigerian cultural film research"
        canonical="https://oludamolaadebowale.com/research-film-projects"
        ogType="website"
        schemaJson={researchFilmSchema}
      />
      <Navigation />

      {/* Hero */}
      <div className="pt-20">
        <div className="w-full py-24 md:py-32" style={{ backgroundColor: '#2c2420' }}>
          <div className="flex flex-col items-center justify-center text-center px-6">
            <nav className="flex items-center gap-2 text-white/80 text-xs font-sans mb-8">
              <Link to="/" className="hover:text-white transition-colors cursor-pointer font-medium">Home</Link>
              <i className="ri-arrow-right-s-line text-white/60" />
              <span className="text-white font-semibold">Research Film Projects</span>
            </nav>
            <p className="text-xs uppercase tracking-widest text-[#b89a6a] font-semibold mb-5 font-sans">
              FILM & DOCUMENTARY
            </p>
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Research Film Projects
            </h1>
            <p className="font-sans text-base text-white/60 max-w-2xl">
              This is a selection of film projects I have contributed to as an archivist and cultural historian, primarily documentary research works that explore Nigerian history, culture, and identity. These projects are grounded in rare archival materials, oral testimonies, and rigorous historical scholarship.
            </p>
          </div>
        </div>
      </div>

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div ref={heroRef} className={`mb-12 ${revealClass(heroVisible)}`}>
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-gray-900">
            Research Film Projects
          </h2>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filmProjects.map((project, i) => (
            <div
              key={project.id}
              className={`bg-white rounded-lg overflow-hidden group hover:translate-y-[-2px] transition-transform duration-300 border border-stone-100 ${revealClass(gridVisible, Math.min(i * 100, 400))}`}
            >
              <Link to={`/project/${project.slug}`} className="block w-full h-52 overflow-hidden bg-stone-100">
                <img
                  src={project.image}
                  alt={project.title}
                  title={project.title + ' | Research Film by Oludamola Adebowale'}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-6">
                <div className="flex items-center justify-end mb-2 flex-wrap gap-2">
                  {project.year && (
                    <span className="text-xs text-gray-400 font-sans">{project.year}</span>
                  )}
                </div>
                <h3 className="font-serif text-lg font-semibold text-gray-900 mb-2 leading-snug">
                  <Link to={`/project/${project.slug}`} className="hover:text-[#b89a6a] transition-colors cursor-pointer">
                    {project.title}
                  </Link>
                </h3>
                <p className="font-sans text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4">
                  {project.description}
                </p>
                <Link
                  to={`/project/${project.slug}`}
                  className="inline-flex items-center gap-1 font-sans text-xs uppercase tracking-wider text-[#b89a6a] hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Read More <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Footer strip */}
      <div className="bg-[#f9f6f1] border-t border-stone-200 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="font-sans text-sm text-gray-500">
            Interested in collaborating on a research film or archival project?{' '}
            <Link to="/press#contact" className="text-gray-900 underline underline-offset-2 hover:text-[#b89a6a] transition-colors cursor-pointer">
              Get in touch
            </Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
